import React, { useState, useEffect } from 'react';

function ApprovalModal({ isOpen, onClose, onSubmit, requisition, currentUser, originalUser, isMasquerading }) {
  const [comment, setComment] = useState('Approved');
  const [loading, setLoading] = useState(false);
  
  // Auto-approval setting - load from localStorage
  const [autoApproveNext, setAutoApproveNext] = useState(() => {
    const stored = localStorage.getItem('requisition_auto_approve_next');
    return stored === null ? true : stored === 'true'; // Default to true if not set
  });

  // Check if current user is the next approver
  const isUserNextApprover = () => {
    if (!requisition || !currentUser) return false;
    
    const currentStatus = parseInt(requisition.requisition_status) || 0;
    const requiredApprovals = parseInt(requisition.specification_approvals) || 0;
    const userId = currentUser.user_id || currentUser.id;
    
    // If already fully approved, no next approver
    if (currentStatus >= requiredApprovals) return false;
    
    // Determine the next approval level (after current approval will be processed)
    const nextApproverLevel = currentStatus + 2; // +1 for current approval, +1 for next level
    
    // Check if this level exists
    if (nextApproverLevel > requiredApprovals) return false;
    
    // Check which approver is needed for the next level
    let nextApproverId = null;
    
    switch (nextApproverLevel) {
      case 1:
        nextApproverId = requisition.mid;
        break;
      case 2:
        nextApproverId = requisition.second;
        break;
      case 3:
        nextApproverId = requisition.approval;
        break;
      default:
        return false;
    }
    
    // Compare user IDs (handle both string and number types)
    return nextApproverId && String(nextApproverId) === String(userId);
  };

  const showAutoApproveToggle = isUserNextApprover();

  // Save to localStorage whenever the setting changes
  useEffect(() => {
    localStorage.setItem('requisition_auto_approve_next', autoApproveNext.toString());
  }, [autoApproveNext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setLoading(true);
    try {
      const approvalData = {
        comment,
        comment_approved: 1, // Set approval flag for backend
        // Only send auto_approve_next if the toggle is visible (user is next approver)
        auto_approve_next: showAutoApproveToggle ? autoApproveNext : false
      };
      
      // Debug logging to help troubleshoot
      console.log('🔍 ApprovalModal: Submitting approval data:', approvalData);
      console.log('🔍 ApprovalModal: Show auto-approve toggle:', showAutoApproveToggle);
      console.log('🔍 ApprovalModal: User is next approver:', showAutoApproveToggle);
      
      await onSubmit(approvalData);
      onClose();
    } catch (error) {
      console.error('Error submitting approval:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center pb-3">
          <h3 className="text-lg font-bold text-gray-900">
            Approve Requisition
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Show masquerading context if applicable */}
        {isMasquerading && currentUser && originalUser && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-orange-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm">
                <span className="font-medium text-orange-800">Approving as: </span>
                <span className="text-orange-700">
                  User ID: {currentUser.user_id} ({currentUser.user_full_name || currentUser.user_name || currentUser.name || 'Name not available'})
                </span>
              </div>
            </div>
          </div>
        )}
        
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-green-700">
              <span className="font-medium">This will approve the requisition and add an approval comment.</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Auto-Approval Toggle - Only show if user is the next approver */}
          {showAutoApproveToggle && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="auto-approve-toggle" className="flex items-center cursor-pointer">
                    <div className="mr-3">
                      <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        Auto-approve EPR-{requisition?.requisition_number || 'this requisition'}
                      </div>
                      <div className="text-xs text-gray-600">
                        You are also the next approver - automatically process your next approval to save time.
                      </div>
                    </div>
                  </label>
                </div>
                <div className="ml-3">
                  <button
                    type="button"
                    id="auto-approve-toggle"
                    onClick={() => setAutoApproveNext(!autoApproveNext)}
                    className={`${
                      autoApproveNext ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    role="switch"
                    aria-checked={autoApproveNext}
                    aria-labelledby="auto-approve-label"
                  >
                    <span
                      className={`${
                        autoApproveNext ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </button>
                </div>
              </div>
              {autoApproveNext && (
                <div className="mt-2 text-xs text-blue-700 flex items-start">
                  <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span>
                    The system will automatically process your next approval.
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="approval-comment" className="block text-sm font-medium text-gray-700 mb-1">
              Approval Comment
            </label>
            <textarea
              id="approval-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter your approval comment..."
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !comment.trim()}
              className={`px-4 py-2 rounded-md disabled:cursor-not-allowed flex items-center justify-center ${
                loading 
                  ? 'bg-green-400 text-white cursor-wait' 
                  : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
              }`}
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? 'Approving...' : 'Approve'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ApprovalModal;